import React, { useState, useEffect } from 'react';
import { batchSearchAPI, feedFormulasAPI, customersAPI } from '../services/api';
import '../styles/BatchSearch.css';

interface BatchSearchResult {
  id: number;
  batch_number: string;
  formula_id: number;
  formula_name: string;
  formula_code: string;
  feed_form: string;
  feed_form_display: string;
  status: string;
  status_display: string;
  planned_weight: number;
  actual_weight: number | null;
  planned_date: string | null;
  started_at: string | null;
  completed_at: string | null;
  completed_by: string | null;
  orders_count: number;
  created_at: string;
}

interface Ingredient {
  raw_material_id: number;
  raw_material_name: string;
  raw_material_sku: string;
  recipe_percentage: number;
  planned_quantity: number;
  actual_quantity: number;
  variance: number;
  is_added: boolean;
  added_at: string | null;
  added_by: string | null;
}

interface OrderAllocation {
  allocation_id: number;
  order_id: number;
  order_number: string;
  customer_id: number;
  customer_name: string;
  line_number: number;
  allocated_weight: number;
  status: string;
  order_status: string;
}

interface LabelPrint {
  id: number;
  template_name: string;
  quantity_printed: number;
  milled_date_printed: string | null;
  batch_number_printed: string;
  printed_at: string;
  printed_by: string | null;
  print_status: string;
}

interface TraceabilityData {
  batch: BatchSearchResult & { notes: string };
  ingredients: Ingredient[];
  orders: OrderAllocation[];
  label_prints: LabelPrint[];
  summary: {
    total_ingredients: number;
    total_orders: number;
    total_labels_printed: number;
  };
}

interface Formula {
  id: number;
  name: string;
  code: string;
}

interface Customer {
  id: number;
  name: string;
}

// Helper to safely extract array from API response
const extractArray = (data: any): any[] => {
  if (Array.isArray(data)) return data;
  if (data?.results && Array.isArray(data.results)) return data.results;
  if (data?.data && Array.isArray(data.data)) return data.data;
  return [];
};

const BatchSearch: React.FC = () => {
  // Search state
  const [batchNumber, setBatchNumber] = useState('');
  const [formulaId, setFormulaId] = useState<number | ''>('');
  const [customerId, setCustomerId] = useState<number | ''>('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Data state
  const [results, setResults] = useState<BatchSearchResult[]>([]);
  const [formulas, setFormulas] = useState<Formula[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Pagination
  const PAGE_SIZE = 50;
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Traceability modal
  const [showTraceModal, setShowTraceModal] = useState(false);
  const [traceData, setTraceData] = useState<TraceabilityData | null>(null);
  const [loadingTrace, setLoadingTrace] = useState(false);

  useEffect(() => {
    loadFormulas();
    loadCustomers();
  }, []);

  const loadFormulas = async () => {
    try {
      const response = await feedFormulasAPI.getAll();
      setFormulas(extractArray(response.data));
    } catch (err) {
      console.error('Error loading formulas:', err);
    }
  };

  const loadCustomers = async () => {
    try {
      const response = await customersAPI.getAll();
      setCustomers(extractArray(response.data));
    } catch (err) {
      console.error('Error loading customers:', err);
    }
  };

  const handleSearch = async (page = 1) => {
    try {
      setLoading(true);
      setError('');

      const params: any = {
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
      };
      if (batchNumber) params.batch_number = batchNumber;
      if (formulaId) params.formula_id = formulaId;
      if (customerId) params.customer_id = customerId;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (statusFilter) params.status = statusFilter;

      const response = await batchSearchAPI.search(params);
      const data = response.data;

      // Handle both paginated and non-paginated responses
      if (data?.count !== undefined) {
        setResults(extractArray(data));
        setTotalCount(data.count);
      } else {
        const arr = extractArray(data);
        setResults(arr);
        setTotalCount(arr.length);
      }
      setCurrentPage(page);
    } catch (err: any) {
      console.error('Error searching batches:', err);
      setError('Failed to search batches');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    handleSearch(newPage);
  };

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const handleViewTraceability = async (batchId: number) => {
    try {
      setLoadingTrace(true);
      const response = await batchSearchAPI.getTraceability(batchId);
      setTraceData(response.data);
      setShowTraceModal(true);
    } catch (err: any) {
      console.error('Error loading traceability:', err);
      alert('Failed to load batch details');
    } finally {
      setLoadingTrace(false);
    }
  };

  const handleClearFilters = () => {
    setBatchNumber('');
    setFormulaId('');
    setCustomerId('');
    setDateFrom('');
    setDateTo('');
    setStatusFilter('');
    setResults([]);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'COMPLETED':
      case 'MILLED':
        return 'status-completed';
      case 'IN_PRODUCTION':
      case 'IN_PROGRESS':
        return 'status-in-progress';
      case 'PLANNED':
        return 'status-planned';
      case 'CANCELLED':
        return 'status-cancelled';
      default:
        return '';
    }
  };

  return (
    <div className="batch-search-page">
      <div className="page-header">
        <h1>Batch Lookup</h1>
        <p className="page-subtitle">Search production batches for traceability and quality tracking</p>
      </div>

      {/* Search Filters */}
      <div className="search-filters-card">
        <div className="filters-grid">
          <div className="filter-group">
            <label>Batch Number</label>
            <input
              type="text"
              className="form-input"
              placeholder="e.g., BATCH-2025-001"
              value={batchNumber}
              onChange={e => setBatchNumber(e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>Feed Formula</label>
            <select
              className="form-select"
              value={formulaId}
              onChange={e => setFormulaId(e.target.value ? parseInt(e.target.value) : '')}
            >
              <option value="">All Formulas</option>
              {formulas.map(f => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Customer</label>
            <select
              className="form-select"
              value={customerId}
              onChange={e => setCustomerId(e.target.value ? parseInt(e.target.value) : '')}
            >
              <option value="">All Customers</option>
              {customers.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Status</label>
            <select
              className="form-select"
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
            >
              <option value="">All Statuses</option>
              <option value="PLANNED">Planned</option>
              <option value="IN_PRODUCTION,IN_PROGRESS">In Production</option>
              <option value="COMPLETED,MILLED">Completed</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Date From</label>
            <input
              type="date"
              className="form-input"
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>Date To</label>
            <input
              type="date"
              className="form-input"
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
            />
          </div>
        </div>

        <div className="filter-actions">
          <button className="btn btn-secondary" onClick={handleClearFilters}>
            Clear
          </button>
          <button className="btn btn-primary" onClick={() => handleSearch(1)} disabled={loading}>
            {loading ? 'Searching...' : 'Search'}
          </button>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Search Results */}
      {results.length > 0 && (
        <div className="results-section">
          <div className="results-header">
            <h2>Search Results ({totalCount > results.length ? totalCount.toLocaleString() : results.length})</h2>
            {totalPages > 1 && (
              <div className="pagination-info">
                Showing {((currentPage - 1) * PAGE_SIZE) + 1}-{Math.min(currentPage * PAGE_SIZE, totalCount)} of {totalCount}
              </div>
            )}
          </div>
          <div className="results-table-container">
            <table className="results-table">
              <thead>
                <tr>
                  <th>Batch Number</th>
                  <th>Formula</th>
                  <th>Form</th>
                  <th>Weight</th>
                  <th>Status</th>
                  <th>Milled</th>
                  <th>Orders</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {results.map(batch => (
                  <tr key={batch.id}>
                    <td className="batch-number-cell">{batch.batch_number}</td>
                    <td>{batch.formula_name}</td>
                    <td>{batch.feed_form_display}</td>
                    <td className="weight-cell">
                      {batch.actual_weight
                        ? `${batch.actual_weight.toLocaleString()} lbs`
                        : `${batch.planned_weight.toLocaleString()} lbs (planned)`}
                    </td>
                    <td>
                      <span className={`status-badge ${getStatusClass(batch.status)}`}>
                        {batch.status_display}
                      </span>
                    </td>
                    <td>{formatDate(batch.completed_at)}</td>
                    <td className="orders-cell">{batch.orders_count}</td>
                    <td>
                      <button
                        className="btn btn-sm btn-outline"
                        onClick={() => handleViewTraceability(batch.id)}
                        disabled={loadingTrace}
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="pagination-controls">
              <button
                className="btn btn-sm btn-secondary"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage <= 1 || loading}
              >
                &larr; Previous
              </button>
              <span className="page-indicator">
                Page {currentPage} of {totalPages}
              </span>
              <button
                className="btn btn-sm btn-secondary"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= totalPages || loading}
              >
                Next &rarr;
              </button>
            </div>
          )}
        </div>
      )}

      {results.length === 0 && !loading && (
        <div className="empty-state">
          <p>Enter search criteria above to find batches.</p>
        </div>
      )}

      {/* Traceability Modal */}
      {showTraceModal && traceData && (
        <div className="modal-overlay" onClick={() => setShowTraceModal(false)}>
          <div className="modal modal-large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Batch Traceability: {traceData.batch.batch_number}</h3>
              <button className="modal-close" onClick={() => setShowTraceModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {/* Batch Summary */}
              <div className="trace-section">
                <h4>Batch Information</h4>
                <div className="trace-info-grid">
                  <div className="trace-info-item">
                    <span className="label">Formula:</span>
                    <span className="value">{traceData.batch.formula_name}</span>
                  </div>
                  <div className="trace-info-item">
                    <span className="label">Feed Form:</span>
                    <span className="value">{traceData.batch.feed_form_display}</span>
                  </div>
                  <div className="trace-info-item">
                    <span className="label">Status:</span>
                    <span className={`status-badge ${getStatusClass(traceData.batch.status)}`}>
                      {traceData.batch.status_display}
                    </span>
                  </div>
                  <div className="trace-info-item">
                    <span className="label">Planned Weight:</span>
                    <span className="value">{traceData.batch.planned_weight.toLocaleString()} lbs</span>
                  </div>
                  <div className="trace-info-item">
                    <span className="label">Actual Weight:</span>
                    <span className="value">
                      {traceData.batch.actual_weight
                        ? `${traceData.batch.actual_weight.toLocaleString()} lbs`
                        : '-'}
                    </span>
                  </div>
                  <div className="trace-info-item">
                    <span className="label">Milled Date:</span>
                    <span className="value">{formatDateTime(traceData.batch.completed_at)}</span>
                  </div>
                  <div className="trace-info-item">
                    <span className="label">Milled By:</span>
                    <span className="value">{traceData.batch.completed_by || '-'}</span>
                  </div>
                  {traceData.batch.notes && (
                    <div className="trace-info-item full-width">
                      <span className="label">Notes:</span>
                      <span className="value">{traceData.batch.notes}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Ingredients Used */}
              <div className="trace-section">
                <h4>Ingredients Used ({traceData.summary?.total_ingredients || 0})</h4>
                <div className="trace-table-container">
                  <table className="trace-table">
                    <thead>
                      <tr>
                        <th>Ingredient</th>
                        <th>SKU</th>
                        <th>Recipe %</th>
                        <th>Planned</th>
                        <th>Actual</th>
                        <th>Variance</th>
                        <th>Added By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(traceData.ingredients || []).map((ing, idx) => (
                        <tr key={idx} className={!ing.is_added ? 'not-added' : ''}>
                          <td>{ing.raw_material_name}</td>
                          <td className="sku-cell">{ing.raw_material_sku}</td>
                          <td>{ing.recipe_percentage.toFixed(2)}%</td>
                          <td>{ing.planned_quantity.toLocaleString()} lbs</td>
                          <td>{ing.actual_quantity.toLocaleString()} lbs</td>
                          <td className={ing.variance !== 0 ? 'variance-highlight' : ''}>
                            {ing.variance !== 0 ? (ing.variance > 0 ? '+' : '') + ing.variance.toFixed(1) : '0'}
                          </td>
                          <td>{ing.added_by || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Orders Fulfilled */}
              <div className="trace-section">
                <h4>Orders Fulfilled ({traceData.summary?.total_orders || 0})</h4>
                {(traceData.orders || []).length > 0 ? (
                  <div className="trace-table-container">
                    <table className="trace-table">
                      <thead>
                        <tr>
                          <th>Order #</th>
                          <th>Customer</th>
                          <th>Allocated</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(traceData.orders || []).map(order => (
                          <tr key={order.allocation_id}>
                            <td className="order-number-cell">#{order.order_number}</td>
                            <td>{order.customer_name}</td>
                            <td>{order.allocated_weight.toLocaleString()} lbs</td>
                            <td>{order.status}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="no-data">No orders allocated to this batch.</p>
                )}
              </div>

              {/* Label Printing History */}
              <div className="trace-section">
                <h4>Labels Printed ({traceData.summary?.total_labels_printed || 0})</h4>
                {(traceData.label_prints || []).length > 0 ? (
                  <div className="trace-table-container">
                    <table className="trace-table">
                      <thead>
                        <tr>
                          <th>Template</th>
                          <th>Quantity</th>
                          <th>Printed At</th>
                          <th>Printed By</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(traceData.label_prints || []).map(lp => (
                          <tr key={lp.id}>
                            <td>{lp.template_name}</td>
                            <td>{lp.quantity_printed}</td>
                            <td>{formatDateTime(lp.printed_at)}</td>
                            <td>{lp.printed_by || '-'}</td>
                            <td>
                              <span className={`print-status ${lp.print_status?.toLowerCase() || 'pending'}`}>
                                {lp.print_status || 'Unknown'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="no-data">No labels printed for this batch.</p>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowTraceModal(false)}>
                Close
              </button>
              <button className="btn btn-outline" onClick={() => window.print()}>
                Print Report
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BatchSearch;
